import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { CustomPlaceholder } from '../../campaign-builder.types';

interface CustomPlaceholdersProps {
  customPlaceholders: CustomPlaceholder[];
  handleAddCustomPlaceholder(title: string, text: string): void;
  handleDeleteCustomPlaceholder(key: string): void;
  handleEditCustomPlaceholder(originalKey: string, newKey: string, newValue: string): void;
  /** Aufklappzustand von außen (alter Builder koppelt die Feld-Schalter daran); ohne Angabe verwaltet die Komponente ihn selbst. */
  expanded?: boolean;
  onExpandedChange?(event: React.SyntheticEvent, expanded: boolean): void;
  /**
   * Optional: Schlüssel-Vorschläge (z. B. Benefits, Gehalt) als Auswahl mit freier Eingabe — immer genau ein Schlüssel pro Platzhalter.
   * `ownKey` = der Schlüssel, der gerade bearbeitet wird (bleibt wählbar); die Komponente blendet nur ab, was der Aufrufer nicht liefert.
   * Ohne Angabe bleibt das Schlüsselfeld ein einfaches Textfeld (alter Builder).
   */
  keySuggestions?(ownKey?: string): string[];
  /** Optional: Erklärung unter dem Feld „Platzhalter-Wert“. */
  valueHint?: string;
  /** Optional: Fehlertext zu einem eingegebenen Schlüssel (leer/null = gültig). */
  keyError?(key: string, ownKey?: string): string | null;
}

/**
 * Eigene Platzhalter (Name + Wert anlegen, bearbeiten, löschen) — aus dem Briefing des alten Builders herausgelöst,
 * damit der Kampagnen-Assistent dieselbe Oberfläche nutzt. Verhalten unverändert.
 */
const CustomPlaceholders: React.FC<CustomPlaceholdersProps> = ({
  customPlaceholders,
  handleAddCustomPlaceholder,
  handleDeleteCustomPlaceholder,
  handleEditCustomPlaceholder,
  expanded,
  onExpandedChange,
  keySuggestions,
  valueHint,
  keyError,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [ownExpanded, setOwnExpanded] = useState<boolean>(customPlaceholders.length > 0);
  const customPlaceholdersExpanded = expanded ?? ownExpanded;

  const [placeholderKey, setPlaceholderKey] = useState('');
  const [placeholderValue, setPlaceholderValue] = useState('');

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editKeyInput, setEditKeyInput] = useState('');
  const [editValueInput, setEditValueInput] = useState('');

  const handleCustomPlaceholdersAccordionChange = (event: React.SyntheticEvent, next: boolean) => {
    if (onExpandedChange) onExpandedChange(event, next);
    else setOwnExpanded(next);
  };

  const handleSavePlaceholder = () => {
    if (!placeholderKey.trim() || !placeholderValue.trim() || (keyError && keyError(placeholderKey))) return;
    handleAddCustomPlaceholder(placeholderKey.trim(), placeholderValue.trim());
    setPlaceholderKey('');
    setPlaceholderValue('');
  };

  /** Schlüsselfeld: mit Vorschlägen eine Einzelauswahl mit freier Eingabe, sonst ein einfaches Textfeld. */
  const renderKeyField = (value: string, onChange: (next: string) => void, ownKey?: string) => {
    if (!keySuggestions) return <PaiTextArea label={t('placeholderKey')} value={value} rows={1} onChange={(e) => onChange(e.target.value)} />;
    const error = value.trim() ? keyError?.(value, ownKey) ?? null : null;
    return (
      <Autocomplete
        freeSolo
        options={keySuggestions(ownKey)}
        inputValue={value}
        onInputChange={(_e, next) => onChange(next)}
        renderInput={(params) => (
          <TextField {...params} size="small" label={t('placeholderKey')} error={!!error} helperText={error ?? undefined} />
        )}
      />
    );
  };

  // lock accordion if placeholders exist
  const accordionLocked = customPlaceholders.length > 0;

  return (
  <Accordion
    expanded={customPlaceholdersExpanded}
    onChange={!accordionLocked ? handleCustomPlaceholdersAccordionChange : undefined}
    disableGutters
    square
    className={classes.subtleAccordion}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      className={classes.subtleAccordionSummary}
    >
      <Typography className={classes.subTitle}>{t('customPlaceholders')}</Typography>
      {accordionLocked && (
        <Tooltip title={t('customPlaceholdersLockedTooltip')} arrow placement="right">
          <InfoOutlinedIcon fontSize="small" sx={{ ml: 1 }} />
        </Tooltip>
      )}
    </AccordionSummary>
    <AccordionDetails className={classes.subtleAccordionDetails}>
      <Typography className={classes.greyText} mb={1}>
        {t('customPlaceholdersDescription')}
      </Typography>

      <Stack gap={1} mb={2}>
        {customPlaceholders.map((p) => (
          <Box
            key={p.key}
            className={classes.placeholderItem}
          >
            {editingKey === p.key ? (
              <Stack gap={1}>
                {renderKeyField(editKeyInput, setEditKeyInput, p.key)}
                <PaiTextArea
                  label={t('placeholderValue')}
                  value={editValueInput}
                  rows={4}
                  onChange={(e) => setEditValueInput(e.target.value)}
                />
                {valueHint ? <Typography className={classes.greyText}>{valueHint}</Typography> : null}
                <Stack direction="row" gap={1}>
                  <IconButton
                    size="small"
                    disabled={!editKeyInput.trim() || !!keyError?.(editKeyInput, p.key)}
                    onClick={() => {
                      if (!editKeyInput.trim() || keyError?.(editKeyInput, p.key)) return;
                      handleEditCustomPlaceholder(p.key, editKeyInput, editValueInput);
                      setEditingKey(null);
                    }}
                  >
                    <CheckIcon fontSize="small" color="success" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setEditingKey(null)}>
                    <CloseIcon fontSize="small" color="error" />
                  </IconButton>
                </Stack>
              </Stack>
            ) : (
              <>
                <Typography fontWeight="bold">{p.key}</Typography>
                <Typography>{p.value}</Typography>
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', top: 0, right: 24 }}
                  onClick={() => {
                    setEditingKey(p.key);
                    setEditKeyInput(p.key);
                    setEditValueInput(p.value);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" sx={{ position: 'absolute', top: 0, right: 0 }} onClick={() => handleDeleteCustomPlaceholder(p.key)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
        ))}
      </Stack>

      <Stack gap={1}>
        {renderKeyField(placeholderKey, setPlaceholderKey)}
        <PaiTextArea label={t('placeholderValue')} value={placeholderValue} rows={4} onChange={(e) => setPlaceholderValue(e.target.value)} />
        {valueHint ? <Typography className={classes.greyText}>{valueHint}</Typography> : null}
        <Box sx={{ alignSelf: 'flex-start' }}>
          <PaiButton
            text={t('savePlaceholder')}
            border="rounded"
            size="small"
            action={handleSavePlaceholder}
            disabled={!placeholderKey.trim() || !placeholderValue.trim() || !!(keyError && keyError(placeholderKey))}
          />
        </Box>
      </Stack>
    </AccordionDetails>
  </Accordion>
  );
};

const useStyles = makeStyles()((theme) => ({
  subTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  subtleAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&::before': { display: 'none' },
    border: `1px solid ${theme.palette.greys.lighter}`,
    borderRadius: 4,
  },
  subtleAccordionSummary: {
    minHeight: 40,
    '& .MuiAccordionSummary-content': { marginTop: 0, marginBottom: 0 },
  },
  subtleAccordionDetails: {
    paddingTop: 0,
    backgroundColor: 'transparent',
  },
  placeholderItem: {
    position: 'relative',
    backgroundColor: theme.palette.greys.lightest,
    padding: 8,
    borderRadius: 4,
    border: `1px solid ${theme.palette.greys.lighter}`,
    boxShadow: 'none',
  },
}));

export default CustomPlaceholders;
